/**
 * Share Service - Handles shareable card operations
 *
 * Features:
 * - Create share cards
 * - Get share card data
 * - Track views/clicks
 */

import { createLogger } from '../utils/logger'

const logger = createLogger('shareService')

const API_BASE = '/api/share'

const shareService = {
  /**
   * Create a new shareable card
   * @param {Object} options - Card options
   * @param {string} options.cardType - Type of card (ranking_summary, challenge, level_up, achievement)
   * @param {string} options.strategyType - Challenge strategy type
   * @param {Object} options.data - Card data (publicName, rank, score, etc.)
   * @returns {Promise<{shareId: string, shareUrl: string}>}
   */
  async createShareCard({ cardType, strategyType, data }) {
    try {
      const response = await fetch(`${API_BASE}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ cardType, strategyType, data })
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        const err = new Error(error.message || 'Failed to create share card')
        err.status = response.status
        throw err
      }

      const result = await response.json()
      return {
        shareId: result.data.shareId,
        shareUrl: `${window.location.origin}/share/${result.data.shareId}`
      }
    } catch (error) {
      logger.error('Error creating share card:', error)
      throw error
    }
  },

  /**
   * Get share card data by ID
   * @param {string} shareId - Share card ID
   * @returns {Promise<Object>} Share card data
   */
  async getShareCard(shareId) {
    try {
      const response = await fetch(`${API_BASE}/${shareId}`, {
        method: 'GET',
        credentials: 'include'
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        const err = new Error(error.message || 'Share card not found')
        err.status = response.status
        throw err
      }

      const result = await response.json()
      return result.data
    } catch (error) {
      logger.error('Error fetching share card:', error)
      throw error
    }
  },

  /**
   * Track a click on a share card CTA
   * @param {string} shareId - Share card ID
   */
  async trackClick(shareId) {
    try {
      await fetch(`${API_BASE}/${shareId}/click`, {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      // Silent fail for analytics
      logger.warn('Failed to track click:', error)
    }
  },

  /**
   * Get share image URL for a card
   * @param {string} shareId - Share card ID
   * @returns {string} Image URL
   */
  getShareImageUrl(shareId) {
    return `${API_BASE}/${shareId}/image`
  }
}

export default shareService
